'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  PICTURE_PHASE_TEXT,
  aircraftStopped,
  pictureLeadM,
  settleDurationMs,
  videoLatencyLabel,
  type PictureMotionPhase,
  type VideoLatencyEstimate,
  type VideoLatencyGrade,
} from '@/lib/video-latency';
import type { ManualAuthorityState } from '@/hooks/useManualControl';

/**
 * How far behind the aircraft this picture is — permanently, on the picture.
 *
 * Not a tooltip and not a one-time warning. Our HLS path runs seconds behind
 * (`deploy/mediamtx/mediamtx.yml`, `webrtc: no`), and an operator flying by
 * hand will look at the video whether or not anybody told them once at the
 * start of the session that they should not. So the caveat lives where the eye
 * already is, in every layout the picture appears in, and it carries a MEASURED
 * number rather than a constant that would go stale the first time the stream
 * changed (`lib/video-latency.ts` explains exactly what is and is not
 * measurable from a browser).
 *
 * Three things it says, in this order:
 *
 * 1. **The number, marked `EST`.** Under 400 ms the same element says
 *    `CONTROL GRADE` instead — same component, same position, different words,
 *    so a WebRTC path needs no redesign to be honest about itself. Past 8 s it
 *    says `PICTURE STALE — DO NOT USE` in red, because a stalled stream and a
 *    healthy one must not be told apart by comparing a figure against a
 *    baseline the operator is carrying in their head. With nothing measurable
 *    it says so, and never invents a figure.
 * 2. **What that means in metres**, for the aircraft actually being flown.
 * 3. **Whether the picture has caught up.** Manoeuvre → stop → look is the only
 *    safe method on a delayed picture, and this is the part that tells the
 *    operator the "look" step is ready.
 */

export interface VideoLatencyBadgeProps {
  /** Null = nothing measurable. Rendered as "not measured", never as zero. */
  estimate: VideoLatencyEstimate | null;
  /** Authority state, so the badge can follow manoeuvre → stop → look. */
  manualState?: ManualAuthorityState | null;
  /** Aircraft ground speed, m/s — what turns a delay into a distance. */
  groundSpeedMs?: number | null;
  /** Tighter type for the corner picture-in-picture. Same words, same order. */
  compact?: boolean;
  /**
   * The caller has already anchored a bottom stack and this badge is inside it
   * (the manual stage puts it under the camera control bar), so the badge does
   * not position itself. It still paints edge to edge — the negative margins
   * cancel the stack's own padding, so the bar and the badge read as one block
   * rather than a badge floating inside a lighter panel.
   */
  stacked?: boolean;
}

/**
 * Fixed on-black foregrounds, NOT the theme's status tokens.
 *
 * This badge paints its own ground — `bg-black/85` over a live picture — so a
 * token that follows the theme follows the wrong surface: `--status-amber-fg`
 * is #7F4E00 in the light theme, which is ~3.0:1 on black, and the sentence it
 * colours is the one saying the picture is not a control reference. The
 * `--on-media-*` set is defined once for both themes and every one of them holds
 * >=4.5:1 at this badge's worst case (85% black over a white video frame).
 *
 * Grade is carried by hue AND by which half of the line is coloured, so the four
 * answers are four different shapes rather than four numbers to compare.
 */
const GRADE_CLASS: Record<VideoLatencyGrade, { headline: string; consequence: string }> = {
  control: { headline: 'text-on-media-green-fg', consequence: 'text-on-media-muted-fg' },
  situational: { headline: 'text-on-media-amber-fg', consequence: 'text-on-media-muted-fg' },
  // Red on BOTH halves. A stale picture is not a caveat on a usable readout, it
  // is the readout, and the line has to stop looking like the healthy one.
  stale: { headline: 'text-on-media-red-fg', consequence: 'text-on-media-red-fg' },
  // The colour moves to the OTHER half, which is what makes "no measurement"
  // legible at a glance next to a measured amber figure rather than identical
  // to it. GRAY on the headline is the convention's "not reporting" — the
  // INSTRUMENT is the thing that is silent — and the amber stays on the
  // consequence, because the caution is unchanged by not being able to size it.
  unknown: { headline: 'text-on-media-muted-fg', consequence: 'text-on-media-amber-fg' },
};

export function VideoLatencyBadge({
  estimate,
  manualState = null,
  groundSpeedMs = null,
  compact = false,
  stacked = false,
}: VideoLatencyBadgeProps) {
  const label = videoLatencyLabel(estimate);
  const leadM = pictureLeadM(estimate?.latencyMs ?? null, groundSpeedMs);
  const phase = usePictureMotionPhase(manualState, estimate, groundSpeedMs);
  const grade = GRADE_CLASS[label.grade];
  // A stream that is not keeping up never gets to announce that it caught up.
  // The settle timer was sized from the latency AT THE STOP; if the stream has
  // been drifting since, that timer expiring says nothing about the picture.
  const pictureCurrent = phase === 'current' && label.grade !== 'stale';

  return (
    <div
      data-testid="video-latency-badge"
      data-grade={label.grade}
      className={cn(
        'pointer-events-none border-t border-white/10 bg-black/85 px-2 py-1 uppercase leading-tight tracking-[0.06em]',
        // Anchors itself unless a caller has already put it in a bottom stack.
        // Two elements both claiming `bottom-0` is a z-index fight, and the
        // badge winning it would hide the camera controls underneath. The
        // stack it joins has no padding of its own — each line in it paints
        // its own ground — so this needs no margin correction, only the
        // absence of its own anchor.
        stacked ? '' : 'absolute inset-x-0 bottom-0 z-10',
        compact ? 'text-[9px]' : 'text-[10px]',
      )}
    >
      <p className={cn('font-bold', grade.headline)}>
        {label.headline}
        <span className={grade.consequence}> — {label.consequence}</span>
      </p>

      {/* The delay as a distance, for this aircraft at this speed. Only drawn
          when both halves are actually known — a lead computed from an
          unmeasured latency would be the invented number this badge exists to
          avoid. */}
      {leadM !== null && (
        <p className="text-on-media-muted-fg">
          Aircraft ~{Math.round(leadM)} m ahead of this picture at{' '}
          {(groundSpeedMs ?? 0).toFixed(1)} m/s
        </p>
      )}

      {/* Announced, because this is the transition an operator is waiting on:
          it is the moment the picture is worth looking at again. Rendered even
          while empty — a live region has to be in the DOM before its content
          changes, or the first change is the one nobody hears. */}
      <p
        role="status"
        aria-live="polite"
        className={cn(pictureCurrent ? 'text-on-media-green-fg' : 'text-on-media-muted-fg')}
      >
        {phase === null ? '' : phaseText(phase, estimate, label.grade)}
      </p>

      <span className="sr-only">{label.detail}</span>
    </div>
  );
}

function phaseText(
  phase: PictureMotionPhase,
  estimate: VideoLatencyEstimate | null,
  grade: VideoLatencyGrade,
): string {
  // Whatever the timer thinks: at this delay there is no "caught up" to reach,
  // and the third line must not be the one place the badge sounds reassuring.
  if (grade === 'stale') return 'PICTURE NOT CURRENT — STREAM STALE';
  if (phase === 'settling' && estimate === null) {
    // No measurement means no moment to promise. Saying "settling" and stopping
    // there is the honest version of a progress bar with no known length.
    return 'Picture settling — settle time unknown';
  }
  return PICTURE_PHASE_TEXT[phase];
}

/**
 * Manoeuvre → stop → look, as a state.
 *
 * The clock starts from TELEMETRY, not from `HOVER`.
 *
 * `HOVER` is set optimistically — the instant the stop is dispatched, before the
 * POST resolves — so timing the settle from it omits the command's own trip
 * (COP → API → MQTT → RC → SDK) AND the aircraft's deceleration from up to
 * 5 m/s. The badge used to declare `PICTURE CURRENT` a second or more before it
 * was, on the one surface whose entire job is saying when the picture can be
 * trusted. Telemetry arrives at ~1 Hz and is far fresher than the video, so the
 * first frame reporting ground speed ≈ 0 is a fact about the aircraft rather
 * than a hope about a command, and that is the instant the video's own delay
 * starts counting down.
 *
 * Where the aircraft reports no ground speed at all, there is nothing to wait
 * for and the timer falls back to the optimistic start plus both allowances —
 * see `settleDurationMs`.
 *
 * The settle duration is sampled at the MOMENT of the stop and then left alone:
 * the latency estimate re-measures every second, and restarting the timer on
 * each new sample would mean `PICTURE CURRENT` never arrived at all. The
 * confirmation is latched for the same reason — ground speed hovering either
 * side of the noise floor must not keep resetting the clock.
 */
function usePictureMotionPhase(
  manualState: ManualAuthorityState | null,
  estimate: VideoLatencyEstimate | null,
  groundSpeedMs: number | null,
): PictureMotionPhase | null {
  const [phase, setPhase] = useState<PictureMotionPhase | null>(null);
  const [stopConfirmed, setStopConfirmed] = useState(false);
  const estimateRef = useRef(estimate);
  estimateRef.current = estimate;
  // Absence of a speed field is a property of the aircraft, not a moment in the
  // stop, so it is read at the start of the episode rather than latched.
  const speedReported = typeof groundSpeedMs === 'number' && Number.isFinite(groundSpeedMs);

  useEffect(() => {
    if (manualState !== 'HOVER') {
      setStopConfirmed(false);
      return;
    }
    if (aircraftStopped(groundSpeedMs)) setStopConfirmed(true);
  }, [manualState, groundSpeedMs]);

  useEffect(() => {
    if (manualState === 'LIVE') {
      setPhase('trailing');
      return;
    }
    if (manualState !== 'HOVER') {
      setPhase(null);
      return;
    }

    setPhase('settling');
    // Still slowing down, and telemetry can say so. No clock yet — the picture
    // cannot have caught up with a manoeuvre that is still happening.
    if (speedReported && !stopConfirmed) return;
    const settleMs = settleDurationMs(estimateRef.current, { stopConfirmed });
    if (settleMs === null) return;
    const id = setTimeout(() => setPhase('current'), settleMs);
    return () => clearTimeout(id);
  }, [manualState, speedReported, stopConfirmed]);

  return phase;
}
